import isEmpty from 'lodash/isEmpty';
import React, { Component } from 'react';
import Data from '../data';
import ServiceUtil from '../data/services/ServiceUtil';
import UI from '../ui';
import Utils from '../utils';
import Filter from './Filter.component';
import Header from './Header.component';
import RateAdvisorUtil from './RateAdvisorUtil';
import { RateCallStatus } from './RateCallStatus.enum';
import Services from './Services.component';
import ValidationError from './ValidationError.component';

const { Carrier, ServiceDataHelper, ServiceType, PackageType } = Data;
const { Spinner } = UI;
const { Communication, CommunicationStatus, Logger, Session, Util } = Utils;

class RateAdvisor extends Component {
	constructor(props) {
		super(props);

		const { ratingData } = props;
		const { RateAdvisorData } = Session.getPreferences();
		const parsedRateAdvisorData = !isEmpty(RateAdvisorData)
			? JSON.parse(RateAdvisorData)
			: {};

		this.state = {
			ratingData,
			rateAdvisorData: RateAdvisorUtil.getRateAdvisorUserPreference(
				parsedRateAdvisorData
			),
			serviceResults: [],

			// filtered services to display in the UI based on left filter selection - An array of service objects
			filteredServices: [],

			getRatesCallStatus: null,
			isLoading: false,
		};
	}

	componentDidMount() {
		const { ratingData } = this.state;

		this.updateServiceRates(ratingData);
	}

	updateServiceRates = async ratingData => {
		this.setState({
			isLoading: true,
			ratingData,
		});
		const getRatesParams = RateAdvisorUtil.buildGetRatesPayload(ratingData);
		const getRatesResponse = await Communication.getRates(getRatesParams);
		const { showFCPackage, showParcelSelect, showGroundAdvantage } = this.props;

		if (Communication.isSuccessful(getRatesResponse)) {
			const { upsGroundETA } = this.state;
			const rates = getRatesResponse.data.filter(rate => {
				const { serviceType, packagingType } = Util.toCamelCase(rate);

				// refer to WEBAPPS-37328 for the rate case filter logic
				if (
					serviceType === ServiceType.UspsFirstClassMail &&
					packagingType === PackageType.Package
				) {
					return showFCPackage;
				}

				if (serviceType === ServiceType.UspsParcelSelect) {
					return showParcelSelect;
				}

				if (serviceType === ServiceType.UspsGroundAdvantage) {
					return showGroundAdvantage;
				}

				return true;
			});

			const filteredRates = ServiceUtil.filterBlockedServices(rates);

			const processedResult = RateAdvisorUtil.processRates(
				filteredRates,
				upsGroundETA
			);
			const filteredServices = RateAdvisorUtil.getPreferredServicesToDisplay(
				processedResult.rates
			);

			this.setState({
				rateAdvisorData: processedResult.rateAdvisorData,
				serviceResults: processedResult.rates,
				filteredServices,
				getRatesCallStatus: isEmpty(filteredServices)
					? RateCallStatus.NoData
					: RateCallStatus.HasData,
				isLoading: false,
			});
		} else {
			this.setState({
				getRatesCallStatus:
					getRatesResponse.status === CommunicationStatus.Failure
						? RateCallStatus.NoData
						: RateCallStatus.Error,
				isLoading: false,
			});
		}
	};

	onHeaderInputChange = headerInputs => {
		const { ratingData } = this.state;
		const newRatingData = {
			...ratingData,
			...headerInputs,
		};

		this.updateServiceRates(newRatingData);
	};

	onFiltersChange = (fieldName, data) => {
		const { serviceResults } = this.state;

		// save the filters change in RateAdvisorUtil class
		const rateAdvisorData = RateAdvisorUtil.saveRateAdvisorUserPreference(
			fieldName,
			data
		);

		// build the new set of filtered services with the filters change
		const filteredServices =
			RateAdvisorUtil.getPreferredServicesToDisplay(serviceResults);

		this.setState({
			rateAdvisorData,
			filteredServices,
			getRatesCallStatus: isEmpty(filteredServices)
				? RateCallStatus.NoData
				: RateCallStatus.HasData,
		});
	};

	onServiceSelection = async selectedData => {
		const { completionCallback } = this.props;
		const { ratingData } = this.state;
		const { weightLb, weightOz, width, length, height, toCountryCode } =
			ratingData;

		// check if the country code is domestic
		const isCountryCodeDomestic = /AS|GU|MH|MP|PW|PR|VI|US/i.test(
			toCountryCode
		);
		const residentialIndicator =
			(selectedData.carrier === Carrier.UPS ||
				selectedData.carrier === Carrier.DHLExpress) &&
			!isCountryCodeDomestic;

		// prevent logger call from aborting when the iframe closes, or before calling completionCallback
		await Logger.logToServer({
			category: 'ui_interaction',
			ui_element: 'ra_CTA',
			value: `${selectedData.serviceType} + ${selectedData.packageType}`,
		});

		// convert SERA data to SWSIM data before passing it back to Mail
		const swsimData = ServiceDataHelper.translateDataToSWSIM(selectedData, {
			width: width || 0,
			height: height || 0,
			length: length || 0,
		});

		completionCallback({
			Carrier: swsimData.carrier,
			Service: swsimData.serviceType,
			Package: swsimData.packageType,
			WeightLbs: weightLb,
			WeightOz: weightOz,
			Width: width,
			Length: length,
			Height: height,
			ResidentialIndicator: residentialIndicator,
		});
	};

	onRetryButtonClick = () => {
		const { ratingData } = this.state;

		this.updateServiceRates(ratingData);
	};

	render() {
		const {
			ratingData,
			rateAdvisorData,
			filteredServices,
			serviceResults,
			getRatesCallStatus,
			isLoading,
		} = this.state;

		return (
			<div className="common-rate-advisor">
				<Header
					ratingData={ratingData}
					onHeaderInputChange={this.onHeaderInputChange}
				/>

				<div className="common-rate-advisor-center-content">
					<Filter
						serviceResults={serviceResults}
						rateAdvisorData={rateAdvisorData}
						onFiltersChange={this.onFiltersChange}
					/>

					{getRatesCallStatus === RateCallStatus.HasData && (
						<Services
							filteredServices={filteredServices}
							onServiceSelection={this.onServiceSelection}
						/>
					)}

					{(getRatesCallStatus === RateCallStatus.NoData ||
						getRatesCallStatus === RateCallStatus.Error) && (
						<ValidationError
							type={getRatesCallStatus}
							onRetryClick={this.onRetryButtonClick}
						/>
					)}

					{isLoading && <Spinner />}
				</div>
			</div>
		);
	}
}

export default RateAdvisor;
